class CustomCursor extends HTMLElement {
  /** @type {HTMLElement | null} */
  cursor = null;
  /** @type {Array<() => void>} */
  cleanupFunctions = [];
  /** @type {WeakMap<HTMLIFrameElement, { win: Window, handler: (e: PointerEvent) => void }>} */
  iframeMoveHandlers = new WeakMap();
  /** @type {MutationObserver | null} */
  observer = null;
  /** @type {number} */
  suspendTrackingCount = 0;
  /** @type {number} */
  focusSuspendedCount = 0;
  /** @type {WeakMap<HTMLIFrameElement, () => void>} */
  iframeCleanupMap = new WeakMap();
  /** @type {WeakMap<HTMLIFrameElement, boolean>} */
  iframeHiddenState = new WeakMap();
  /** @type {WeakMap<HTMLIFrameElement, boolean>} */
  iframeFocusState = new WeakMap();
  /** @type {WeakMap<HTMLIFrameElement, () => void>} */
  iframeLoadCleanupMap = new WeakMap();
  /** @type {WeakMap<HTMLIFrameElement, () => void>} */
  iframePointerMoveCleanupMap = new WeakMap();
  /** @type {number | null} */
  setupIframeTimeout = null;

  constructor() {
    super();
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handleDocLeave = this.handleDocLeave.bind(this);
    this.handleDocEnter = this.handleDocEnter.bind(this);
    this.onMutate = this.onMutate.bind(this);
  }

  connectedCallback() {
    this.cursor = /** @type {HTMLElement | null} */ (this.querySelector('.custom-cursor'));
    if (!this.cursor) return;

    window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', this.handleDocLeave);
    document.documentElement.addEventListener('mouseenter', this.handleDocEnter);

    const boundWindowBlur = () => this.#hideCursor();
    const boundWindowFocus = () => this.#showCursorIfNotSuspended();
    window.addEventListener('blur', boundWindowBlur);
    window.addEventListener('focus', boundWindowFocus);
    this.cleanupFunctions.push(() => {
      window.removeEventListener('blur', boundWindowBlur);
      window.removeEventListener('focus', boundWindowFocus);
    });

    this.setupIframeSupport();

    this.observer = new MutationObserver(this.onMutate);
    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    this.cleanupFunctions.push(() => {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.setupIframeTimeout) {
        clearTimeout(this.setupIframeTimeout);
        this.setupIframeTimeout = null;
      }
    });
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    document.documentElement.removeEventListener('mouseleave', this.handleDocLeave);
    document.documentElement.removeEventListener('mouseenter', this.handleDocEnter);

    if (this.setupIframeTimeout) {
      clearTimeout(this.setupIframeTimeout);
      this.setupIframeTimeout = null;
    }

    for (let i = this.cleanupFunctions.length - 1; i >= 0; i--) {
      const cleanup = this.cleanupFunctions[i];
      if (cleanup) {
        try {
          cleanup();
        } catch (e) {
          // Ignore cleanup errors to ensure all cleanup functions execute
        }
      }
    }
    this.cleanupFunctions = [];
    this.suspendTrackingCount = 0;
    this.focusSuspendedCount = 0;
  }

  /** @param {PointerEvent} e */
  handlePointerMove(e) {
    if (!this.cursor || this.suspendTrackingCount > 0 || this.focusSuspendedCount > 0) return;
    this.cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
    this.#showCursor();
  }

  handleDocLeave() {
    if (!this.cursor) return;
    this.#hideCursor();
  }

  handleDocEnter() {
    if (!this.cursor) return;
    this.#showCursorIfNotSuspended();
  }

  #hideCursor() {
    if (this.cursor) {
      this.cursor.classList.add('is-hidden');
      this.cursor.style.visibility = 'hidden';
    }
  }

  #showCursor() {
    if (this.cursor) {
      this.cursor.classList.remove('is-hidden');
      this.cursor.style.visibility = '';
    }
  }

  #showCursorIfNotSuspended() {
    if (this.suspendTrackingCount === 0 && this.focusSuspendedCount === 0) {
      this.#showCursor();
    }
  }

  /** @param {MutationRecord[]} mutations */
  onMutate(mutations) {
    let hasIframeChange = false;

    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];
      if (!mutation || mutation.type !== 'childList') continue;

      for (let j = 0; j < mutation.removedNodes.length; j++) {
        const node = mutation.removedNodes[j];
        if (!node || node.nodeType !== Node.ELEMENT_NODE) continue;
        const element = /** @type {Element} */ (node);
        if (element.tagName === 'IFRAME') {
          this.#cleanupIframe(/** @type {HTMLIFrameElement} */ (element));
          hasIframeChange = true;
        } else if (element.querySelectorAll) {
          const iframes = element.querySelectorAll('iframe');
          for (let k = 0; k < iframes.length; k++) {
            const iframe = iframes[k];
            if (iframe) {
              this.#cleanupIframe(iframe);
              hasIframeChange = true;
            }
          }
        }
      }

      if (mutation.addedNodes.length > 0) {
        for (let j = 0; j < mutation.addedNodes.length; j++) {
          const node = mutation.addedNodes[j];
          if (!node || node.nodeType !== Node.ELEMENT_NODE) continue;
          const element = /** @type {Element} */ (node);
          if (element.tagName === 'IFRAME' || element.querySelector('iframe')) {
            hasIframeChange = true;
            break;
          }
        }
      }
    }

    if (hasIframeChange) {
      if (this.setupIframeTimeout) {
        clearTimeout(this.setupIframeTimeout);
      }
      this.setupIframeTimeout = window.setTimeout(() => {
        this.setupIframeSupport();
        this.setupIframeTimeout = null;
      }, 0);
    }
  }

  /** @param {() => void} cleanupFn */
  #removeFromCleanupFunctions(cleanupFn) {
    const index = this.cleanupFunctions.indexOf(cleanupFn);
    if (index > -1) {
      this.cleanupFunctions.splice(index, 1);
    }
  }

  /** @param {HTMLIFrameElement} iframe */
  #cleanupSameOriginHandlers(iframe) {
    const pointerMoveCleanup = this.iframePointerMoveCleanupMap.get(iframe);
    if (pointerMoveCleanup) {
      pointerMoveCleanup();
    }
    const loadCleanup = this.iframeLoadCleanupMap.get(iframe);
    if (loadCleanup) {
      loadCleanup();
    }
  }

  /** @param {HTMLIFrameElement} iframe */
  #cleanupIframe(iframe) {
    const cleanup = this.iframeCleanupMap.get(iframe);
    if (cleanup) {
      cleanup();
    }
    this.#cleanupSameOriginHandlers(iframe);
  }

  /** @param {HTMLIFrameElement} iframe */
  #bindCrossOriginHideShow(iframe) {
    if (this.iframeCleanupMap.has(iframe)) return;

    this.iframeHiddenState.set(iframe, false);
    this.iframeFocusState.set(iframe, false);
    
    const hideOnMouseEnter = () => {
      if (this.iframeHiddenState.get(iframe) === true) return;
      this.iframeHiddenState.set(iframe, true);
      this.suspendTrackingCount++;
      this.#hideCursor();
    };
    const showOnMouseLeave = () => {
      if (this.iframeHiddenState.get(iframe) !== true) return;
      this.iframeHiddenState.set(iframe, false);
      this.suspendTrackingCount = Math.max(0, this.suspendTrackingCount - 1);
      if (this.suspendTrackingCount === 0 && this.focusSuspendedCount === 0) {
        this.#showCursor();
      }
    };

    const hideOnFocusIn = () => {
      if (this.iframeFocusState.get(iframe) === true) return;
      this.iframeFocusState.set(iframe, true);
      this.focusSuspendedCount++;
      this.#hideCursor();
    };
    const showOnFocusOut = () => {
      if (this.iframeFocusState.get(iframe) !== true) return;
      this.iframeFocusState.set(iframe, false);
      this.focusSuspendedCount = Math.max(0, this.focusSuspendedCount - 1);
      if (this.suspendTrackingCount === 0 && this.focusSuspendedCount === 0) {
        this.#showCursor();
      }
    };

    iframe.addEventListener('mouseenter', hideOnMouseEnter, true);
    iframe.addEventListener('mouseleave', showOnMouseLeave, true);
    iframe.addEventListener('focusin', hideOnFocusIn, true);
    iframe.addEventListener('focusout', showOnFocusOut, true);

    let hasCleanedUp = false;
    const cleanup = () => {
      if (hasCleanedUp) return;
      hasCleanedUp = true;
      
      iframe.removeEventListener('mouseenter', hideOnMouseEnter, true);
      iframe.removeEventListener('mouseleave', showOnMouseLeave, true);
      iframe.removeEventListener('focusin', hideOnFocusIn, true);
      iframe.removeEventListener('focusout', showOnFocusOut, true);
      
      if (!iframe.isConnected) {
        if (this.iframeHiddenState.get(iframe) === true) {
          showOnMouseLeave();
        }
        if (this.iframeFocusState.get(iframe) === true) {
          showOnFocusOut();
        }
        this.iframeHiddenState.delete(iframe);
        this.iframeFocusState.delete(iframe);
      }
      this.iframeCleanupMap.delete(iframe);
      this.#removeFromCleanupFunctions(cleanup);
    };

    this.iframeCleanupMap.set(iframe, cleanup);
    this.cleanupFunctions.push(cleanup);
  }

  setupIframeSupport() {
    if (!this.cursor) return;

    const allIframes = document.querySelectorAll('iframe');
    
    let actuallyHiddenCount = 0;
    let actuallyFocusedCount = 0;
    for (let i = 0; i < allIframes.length; i++) {
      const iframe = allIframes[i];
      if (iframe && this.iframeCleanupMap.has(iframe)) {
        if (this.iframeHiddenState.get(iframe) === true) {
          actuallyHiddenCount++;
        }
        if (this.iframeFocusState.get(iframe) === true) {
          actuallyFocusedCount++;
        }
      }
    }
    if (this.suspendTrackingCount > actuallyHiddenCount) {
      const wasHidden = this.suspendTrackingCount > 0;
      this.suspendTrackingCount = actuallyHiddenCount;
      if (wasHidden && this.suspendTrackingCount === 0 && this.focusSuspendedCount === 0) {
        this.#showCursor();
      }
    }
    if (this.focusSuspendedCount > actuallyFocusedCount) {
      const wasHidden = this.focusSuspendedCount > 0;
      this.focusSuspendedCount = actuallyFocusedCount;
      if (wasHidden && this.focusSuspendedCount === 0 && this.suspendTrackingCount === 0) {
        this.#showCursor();
      }
    }

    /** @param {HTMLIFrameElement} iframe */
    const processIframe = (iframe) => {
      if (!iframe.isConnected) return;
      
      const hasSameOriginHandler = this.iframeMoveHandlers.has(iframe);
      
      if (hasSameOriginHandler) {
        try {
          const win = iframe.contentWindow;
          if (win) {
            void win.document;
            return;
          }
        } catch (e) {
          this.#cleanupSameOriginHandlers(iframe);
        }
      }
      
      if (this.iframeCleanupMap.has(iframe) || this.iframeLoadCleanupMap.has(iframe)) {
        return;
      }
      
      this.iframeHiddenState.delete(iframe);
      this.iframeFocusState.delete(iframe);

      const isSameOrigin = this.isSameOriginIframe(iframe);

      if (!isSameOrigin) {
        this.#bindCrossOriginHideShow(iframe);
        return;
      }

      /** @type {(() => void) | null} */
      let loadCleanup = null;

      const attachSameOrigin = () => {
        try {
          const win = iframe.contentWindow;
          if (!win) return;

          const stored = this.iframeMoveHandlers.get(iframe);
          if (stored?.win === win) {
            try {
              void win.document;
            } catch (e) {
              this.#cleanupSameOriginHandlers(iframe);
              this.#bindCrossOriginHideShow(iframe);
              return;
            }
            return;
          }

          if (stored?.win && stored?.handler) {
            try {
              stored.win.removeEventListener('pointermove', stored.handler);
            } catch (e) {
              // Ignore cleanup errors
            }
          }

          /** @param {PointerEvent} e */
          const onMove = (e) => {
            if (!this.cursor || this.suspendTrackingCount > 0 || this.focusSuspendedCount > 0) return;
            const rect = iframe.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(iframe);
            const borderLeft = Number.parseFloat(computedStyle.borderLeftWidth) || 0;
            const borderTop = Number.parseFloat(computedStyle.borderTopWidth) || 0;
            const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
            const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
            const x = rect.left + borderLeft + paddingLeft + e.clientX;
            const y = rect.top + borderTop + paddingTop + e.clientY;
            this.cursor.style.transform = `translate3d(${x}px, ${y}px, 0)`;
            this.#showCursor();
          };

          this.iframeMoveHandlers.set(iframe, { win, handler: onMove });

          win.addEventListener('pointermove', onMove, { passive: true });
          let pointerMoveCleanupExecuted = false;
          const pointerMoveCleanup = () => {
            if (pointerMoveCleanupExecuted) return;
            pointerMoveCleanupExecuted = true;
            try {
              win.removeEventListener('pointermove', onMove);
            } catch (e) {
              // Ignore cleanup errors
            }
            const cur = this.iframeMoveHandlers.get(iframe);
            if (cur?.win === win) {
              this.iframeMoveHandlers.delete(iframe);
            }
            this.iframePointerMoveCleanupMap.delete(iframe);
            this.#removeFromCleanupFunctions(pointerMoveCleanup);
          };
          this.iframePointerMoveCleanupMap.set(iframe, pointerMoveCleanup);
          this.cleanupFunctions.push(pointerMoveCleanup);
        } catch (e) {
          if (loadCleanup) {
            loadCleanup();
          }
          this.#bindCrossOriginHideShow(iframe);
        }
      };

      let loadCleanupExecuted = false;
      loadCleanup = () => {
        if (loadCleanupExecuted) return;
        loadCleanupExecuted = true;
        iframe.removeEventListener('load', attachSameOrigin);
        this.iframeLoadCleanupMap.delete(iframe);
        this.#removeFromCleanupFunctions(/** @type {() => void} */ (loadCleanup));
      };
      iframe.addEventListener('load', attachSameOrigin);
      this.iframeLoadCleanupMap.set(iframe, loadCleanup);
      this.cleanupFunctions.push(loadCleanup);

      try {
        if (iframe.contentDocument?.readyState === 'complete') {
          attachSameOrigin();
        }
      } catch (e) {
        loadCleanup();
        this.#bindCrossOriginHideShow(iframe);
        return;
      }
    };

    for (let i = 0; i < allIframes.length; i++) {
      const iframe = allIframes[i];
      if (iframe) {
        processIframe(iframe);
      }
    }
  }

  /** @param {HTMLIFrameElement} iframe */
  isSameOriginIframe(iframe) {
    const srcdoc = iframe.getAttribute('srcdoc');
    if (srcdoc !== null) {
      return true;
    }

    const src = iframe.getAttribute('src');
    if (!src || src === 'about:blank') {
      return true;
    }

    try {
      const url = new URL(src, window.location.href);
      return url.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }
}

if (!customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursor);
}
